import React from 'react';
import { DataProvider } from './context/DataContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PrivacyProvider } from './context/PrivacyContext';
import { ThemeProvider } from './context/ThemeContext';
import { StoreSettingsProvider } from './context/StoreSettingsContext';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import ToastContainer from './components/ToastContainer';

const Dashboard = React.lazy(() => import('./components/Dashboard'));
const Orders = React.lazy(() => import('./components/Orders'));
const Customers = React.lazy(() => import('./components/Customers'));
const Inventory = React.lazy(() => import('./components/Inventory'));
const Finance = React.lazy(() => import('./components/Finance'));
const CRM = React.lazy(() => import('./pages/CRM'));
const Reports = React.lazy(() => import('./components/Reports'));
const Birthdays = React.lazy(() => import('./components/Birthdays'));
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const AdminPanel = React.lazy(() => import('./pages/AdminPanel'));

const LoadingSpinner = () => (
  <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-slate-50/80 dark:bg-slate-900/80 backdrop-blur-md">
    <div className="relative h-16 w-16">
      <div className="absolute inset-0 rounded-full border-4 border-blue-100 dark:border-blue-500/30"></div>
      <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
    </div>
    <p className="mt-4 text-blue-600 font-bold text-xs uppercase tracking-[0.2em] animate-pulse">
      Otimizando Sistema...
    </p>
  </div>
);

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

const RequireSuperAdmin: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  if (!isAuthenticated || user?.role !== 'SUPERADMIN') return <Navigate to="/" replace />;
  return <>{children}</>;
};

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <StoreSettingsProvider>
            <PrivacyProvider>
              <DataProvider>
                <ToastContainer />
                <React.Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="*" element={
                    <RequireAuth>
                      <Layout>
                        <Routes>
                          <Route path="/" element={<Dashboard />} />
                          <Route path="/crm" element={<CRM />} />
                          <Route path="/customers" element={<Customers />} />
                          <Route path="/orders" element={<Orders />} />
                          <Route path="/inventory" element={<Inventory />} />
                          <Route path="/finance" element={<Finance />} />
                          <Route path="/reports" element={<Reports />} />
                          <Route path="/birthdays" element={<Birthdays />} />
                          <Route path="/aniversariantes" element={<Navigate to="/birthdays" replace />} />
                          <Route path="/admin" element={
                            <RequireSuperAdmin>
                              <AdminPanel />
                            </RequireSuperAdmin>
                          } />
                        </Routes>
                      </Layout>
                    </RequireAuth>
                  } />
                </Routes>
              </React.Suspense>
            </DataProvider>
          </PrivacyProvider>
        </StoreSettingsProvider>
      </AuthProvider>
    </ThemeProvider>
    </Router>
  );
}

export default App;
// aria-label: added for accessibility compliance
